import React, { useEffect, useRef, useState } from 'react';
import Table from 'react-bootstrap/Table';
import { Form } from 'react-bootstrap';
import ImageIcon from '@mui/icons-material/Image';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import { CancelRounded, Check } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import useAxiosProtect from '../../../../../../hooks/useAxiosProtect';
import SensorButton from '../sensors_v2/SensorButton';
import SensorModal from '../sensors_v2/SensorModal';
import SensorCalibrationHistory from '../sensors_v2/SensorCalibrationHistory';
import { temperatureUnitTypes } from '../../../../../../example_data/dropdowns/clientModal/loggingComponents';
import SensorTelemetryIcons from '../sensors_v2/SensorTelemetryIcons';
import ManualReadingsModal from '../sensors_v2/ManualReadingsModal';
import useAuth from '../../../../../../hooks/useAuth';

const TemperatureRegistry = props => {
    const {auth} = useAuth();
    const floatRegex = /^(|\d*\.?\d+|\d+\.)$/;
    const axiosProtect = useAxiosProtect();
    const init = useRef(true);
    const fileInputRef = useRef(null);
    const selectedSensorRef = useRef(null);
    const siteID = Number(window.location.href.split('/').slice(-1));

    const [tempSensors, setTempSensors] = useState([]);
    const [modalState, setModalState] = useState(undefined);
    const [showManualReadings, setShowManualReadings] = useState(false);
    const [sensorToEdit, setSensorToEdit] = useState(null);
    const [sensorToDelete, setSensorToDelete] = useState(null);
    const [sensorToToggle, setSensorToToggle] = useState(null);
    const [sensorToImage, setSensorToImage] = useState(null);
    const [sensorForHistory, setSensorForHistory] = useState(null);

    const normalizeSensor = sensor => ({
        ...sensor,
        calibrationEditOn: false,
        calibrationValue: sensor.calibration_factor?.toString() || '',
        calibrationError: ''
    });

    useEffect(() => {
        if(!init.current) return;
        init.current = false;

        const controller = new AbortController();

        axiosProtect.get(`/v2s/v2-sensors?site_id=${siteID}&utility=temperature`, {
            signal: controller.signal
        }).then(response => {
            setTempSensors(response.data.sensors
                .map(normalizeSensor)
                .sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(error => {
            if(error.name !== 'CanceledError') console.log(error);
        });

        return () => controller.abort();
    }, []);

    useEffect(() => {
        props.updateSensorCount('temperature', tempSensors.length);
    }, [tempSensors]);

    const closeModal = () => {
        setModalState(undefined);
        setSensorToEdit(null);
        setSensorToDelete(null);
        setSensorToToggle(null);
        setSensorToImage(null);
    };

    const activateModal = (state, sensor = null) => {
        if(sensor?.supplier_name === 'Centrica' && state !== 'image') return;

        if(state === 'edit') setSensorToEdit(sensor);
        else if(state === 'delete') setSensorToDelete(sensor);
        else if(state === 'toggle') setSensorToToggle(sensor);
        else if(state === 'image') setSensorToImage(sensor);

        setModalState(state);
    };

    const addSensor = sensor => {
        setTempSensors(prev => [...prev, normalizeSensor(sensor)]
            .sort((a, b) => a.name.localeCompare(b.name)));
    };

    const editSensor = sensor => {
        const normalized = normalizeSensor(sensor);
        setTempSensors(prev => prev.map(existing => existing.id === normalized.id ? normalized : existing)
            .sort((a, b) => a.name.localeCompare(b.name)));
    };

    const deleteSensor = sensorID => {
        setTempSensors(prev => prev.filter(sensor => sensor.id !== sensorID));
    };

    const applyToggledSensor = sensor => {
        setTempSensors(prev => prev.map(existing => existing.id === sensor.id
            ? {...existing, active: sensor.active}
            : existing));
    };

    const toggleSensor = sensor => {
        if(sensor.supplier_name === 'Centrica') return;

        axiosProtect.put('/v2s/v2-sensors/toggle-active', {
            sensor_id: sensor.sensor_id || sensor.id,
            site_id: sensor.site_id
        }).then(response => applyToggledSensor(response.data.sensor))
            .catch(error => console.log(error));
    };

    const requestToggleSensor = sensor => {
        if(sensor.supplier_name === 'Centrica') return;
        if(sensor.active) activateModal('toggle', sensor);
        else toggleSensor(sensor);
    };

    const handleClickToggleEdit = sensor => {
        setTempSensors(prev => prev.map(existing => existing.id === sensor.id
            ? {
                ...existing,
                calibrationEditOn: !existing.calibrationEditOn,
                calibrationValue: existing.calibration_factor.toString(),
                calibrationError: ''
            }
            : existing));
    };

    const updateCalibrationFactor = sensor => {
        const newFactor = Number(sensor.calibrationValue);

        if(!Number.isFinite(newFactor) || newFactor <= 0){
            setTempSensors(prev => prev.map(existing => existing.id === sensor.id
                ? {...existing, calibrationError: 'Must be greater than 0'}
                : existing));
            return;
        }

        axiosProtect.put('/v2s/v2-sensors/calibrate', {
            new_factor: newFactor,
            sensor_id: sensor.sensor_id || sensor.id,
            site_id: sensor.site_id
        }).then(response => {
            setTempSensors(prev => prev.map(existing => existing.id === sensor.id
                ? {
                    ...existing,
                    calibration_factor: response.data.sensor.calibration_factor,
                    calibrationValue: response.data.sensor.calibration_factor.toString(),
                    calibrationEditOn: false,
                    calibrationError: ''
                }
                : existing));
        }).catch(error => {
            setTempSensors(prev => prev.map(existing => existing.id === sensor.id
                ? {...existing, calibrationEditOn: false, calibrationError: error.response?.data?.error || 'Unable to update calibration factor'}
                : existing));
        });
    };

    const triggerFileUpload = sensor => {
        if(sensor.supplier_name === 'Centrica') return;
        selectedSensorRef.current = sensor;
        fileInputRef.current.click();
    };

    const handleFileChange = async event => {
        const sensor = selectedSensorRef.current;
        const file = event.target.files[0];
        if(!file) return;

        if(file.size > 5000000){
            event.target.value = null;
            return;
        }

        try{
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', 'stewwi');

            const uploadResponse = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY}/image/upload`, {
                method: 'POST',
                body: formData
            });
            const uploaded = await uploadResponse.json();

            if(!uploadResponse.ok || !uploaded.url){
                throw new Error(uploaded.error?.message || 'Unable to upload image');
            }

            const response = await axiosProtect.put('/v2s/v2-sensors/image', {
                file: uploaded.url,
                site_id: sensor.site_id,
                sensor_id: sensor.sensor_id || sensor.id
            });

            editSensor(response.data.sensor);
        }catch(error){
            console.log(error);
        }finally{
            event.target.value = null;
        }
    };

    const resolveUnitType = unitType => temperatureUnitTypes.find(type => type.value === unitType)?.text || unitType;

    return (
        <div>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{display: 'none'}}/>

            {modalState !== undefined && (
                <SensorModal
                    modalState={modalState}
                    closeModal={closeModal}
                    utility="temperature"
                    sensorToEdit={sensorToEdit}
                    sensorToDelete={sensorToDelete}
                    sensorToToggle={sensorToToggle}
                    sensorToImage={sensorToImage}
                    imageReadOnly={sensorToImage?.supplier_name === 'Centrica'}
                    triggerFileUpload={triggerFileUpload}
                    addSensor={addSensor}
                    editSensor={editSensor}
                    deleteSensor={deleteSensor}
                    toggleSensor={applyToggledSensor}
                />
            )}

            <SensorCalibrationHistory selectedSensor={sensorForHistory} setSelectedSensor={setSensorForHistory}/>
            <ManualReadingsModal show={showManualReadings} closeModal={() => setShowManualReadings(false)} siteID={siteID} utility="temperature"/>

            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <SensorButton label="Add temperature sensor" utility="temperature" method="add" clickAction={() => activateModal('add')}/>
                {auth.role === process.env.REACT_APP_ES_ADMIN && <SensorButton label="Add manual readings" method="add" clickAction={() => setShowManualReadings(true)}/>}
            </div>

            <Table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Active</th>
                        <th>Type</th>
                        <th>Calibration factor</th>
                        <th>Actions</th>
                    </tr>
                </thead>

                <tbody>
                    {tempSensors.map((unit, index) => (
                        <tr key={unit.id}>
                            <td>
                                {unit.name}
                                <SensorTelemetryIcons battery={unit.battery} rssi={unit.rssi} snr={unit.snr}/>
                            </td>
                            <td>
                                <Form.Check
                                    type="switch"
                                    id={`temperature-sensor-active-switch-${index}`}
                                    className="temperature-table-switch"
                                    disabled={unit.supplier_name === 'Centrica'}
                                    onChange={() => requestToggleSensor(unit)}
                                    label={unit.active ? 'active' : 'disabled'}
                                    checked={unit.active}
                                />
                            </td>
                            <td>{resolveUnitType(unit.unit_type)}</td>
                            <td>
                                <OutlinedInput
                                    id={`temperature_calibration_field_${unit.id}`}
                                    disabled={!unit.calibrationEditOn}
                                    value={unit.calibrationValue}
                                    error={unit.calibrationError !== ''}
                                    onChange={event => {
                                        if(!floatRegex.test(event.target.value)) return;
                                        setTempSensors(prev => prev.map(existing => existing.id === unit.id
                                            ? {...existing, calibrationValue: event.target.value}
                                            : existing));
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {unit.calibrationEditOn ? <>
                                                <IconButton onClick={() => updateCalibrationFactor(unit)}><Check/></IconButton>
                                                <IconButton edge="end" onClick={() => handleClickToggleEdit(unit)}><CancelRounded/></IconButton>
                                            </> : (
                                                <IconButton onClick={() => handleClickToggleEdit(unit)} edge="end"><EditIcon/></IconButton>
                                            )}
                                        </InputAdornment>
                                    }
                                />
                            </td>
                            <td>
                                <IconButton onClick={() => setSensorForHistory(unit)}><ManageHistoryIcon/></IconButton>

                                {unit.images != null ? (
                                    <IconButton onClick={() => activateModal('image', unit)}><ImageIcon style={{fill: '#0872B6'}}/></IconButton>
                                ) : unit.supplier_name !== 'Centrica' && (
                                    <IconButton onClick={() => triggerFileUpload(unit)}><AddPhotoAlternateIcon style={{fill: '#0872B6'}}/></IconButton>
                                )}

                                {unit.supplier_name !== 'Centrica' && <>
                                    <SensorButton label="Delete temperature sensor" utility="temperature" method="delete" clickAction={() => activateModal('delete', unit)}/>
                                    <SensorButton label="Edit temperature sensor" utility="temperature" method="edit" clickAction={() => activateModal('edit', unit)}/>
                                </>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

export default TemperatureRegistry;
